import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ProductFeedbackAPI, FeedbackDraft } from '../../lib/api/productFeedback';
import { getErrorMessage } from '../../lib/api/client';
import { feedbackPage, productReleases, visibleReleases } from '../../lib/productUpdates';
import { hasAcceptedCurrentTos } from '../../lib/tos';
import WhatsNewDialog from './WhatsNewDialog';
import FeedbackDialog from './FeedbackDialog';

interface Controls {
  openUpdates: () => void;
  openFeedback: () => void;
  hasUnread: boolean;
  setOnboardingPending: (pending: boolean) => void;
}
const UpdatesContext = createContext<Controls | undefined>(undefined);
export function useProductUpdates() { return useContext(UpdatesContext); }

function UpdatesSession({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [view, setView] = useState<'updates' | 'feedback' | null>(null);
  const [seen, setSeen] = useState<string[] | null>(null);
  const [otherDialog, setOtherDialog] = useState(false);
  const [onboardingPending, setOnboardingPending] = useState(false);
  const [draft, setDraft] = useState<FeedbackDraft | null>(null);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const sending = useRef(false);
  const submission = useRef<AbortController | null>(null);
  const autoOpened = useRef(false);
  const mounted = useRef(true);
  const releases = visibleReleases(productReleases, user?.company?.mode || '');
  const latest = releases[0];
  const mode = user?.company?.mode;
  const inApp = !!user && !!mode && (location.pathname === `/${mode}` || location.pathname.startsWith(`/${mode}/`));
  const allowed = inApp && !loading && hasAcceptedCurrentTos(user?.tosVersion);
  const hasUnread = !!latest && seen !== null && !seen.includes(latest.id);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; submission.current?.abort(); };
  }, []);

  useEffect(() => {
    if (!allowed) return;
    let active = true;
    ProductFeedbackAPI.seen().then(result => {
      if (active) setSeen(current => Array.from(new Set([...(current || []), ...result.releaseIds])));
    }).catch(() => {
      // Announcements stay manual when the read state is unavailable.
    });
    return () => { active = false; };
  }, [allowed]);

  useEffect(() => {
    const check = () => setOtherDialog(!!document.querySelector('[role="dialog"][aria-modal="true"]:not(.vein-product-dialog), [role="alertdialog"]'));
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['role'] });
    check();
    return () => observer.disconnect();
  }, []);

  const freshDraft = useCallback((releaseId = ''): FeedbackDraft => ({
    requestId: crypto.randomUUID(), category: 'comment', message: '',
    pageContext: feedbackPage(location.pathname), releaseId,
  }), [location.pathname]);

  const markLatestSeen = useCallback(() => {
    if (!latest) return;
    setSeen(current => Array.from(new Set([...(current || []), latest.id])));
    ProductFeedbackAPI.markSeen(latest.id).catch(() => {
      // Keep dismissal for this session; another device may show the update again.
    });
  }, [latest]);

  const open = useCallback((next: 'updates' | 'feedback', releaseId = '') => {
    if (!allowed) return;
    if (next === 'feedback') {
      if (view === 'updates') markLatestSeen();
      setDraft(current => current && (current.message.length || current.category !== 'comment' || sending.current || receipt) ? current : freshDraft(releaseId));
    } else if (view === 'feedback' && receipt) {
      setReceipt('');
      setDraft(null);
    }
    setView(next);
  }, [allowed, freshDraft, receipt, view, markLatestSeen]);

  useEffect(() => {
    if (!allowed || onboardingPending || otherDialog || view || !hasUnread || autoOpened.current || location.pathname !== `/${mode}`) return;
    const timer = window.setTimeout(() => {
      autoOpened.current = true;
      open('updates');
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [allowed, onboardingPending, otherDialog, view, hasUnread, location.pathname, mode, open]);

  const close = () => {
    if (view === 'updates') markLatestSeen();
    if (view === 'feedback' && receipt) {
      setReceipt('');
      setDraft(null);
    }
    autoOpened.current = true;
    setView(null);
  };

  const submit = async () => {
    if (!draft || sending.current) return;
    sending.current = true;
    setSubmitting(true);
    setError('');
    const controller = new AbortController();
    submission.current = controller;
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    try {
      const result = await ProductFeedbackAPI.submit({ ...draft, message: draft.message.trim() }, controller.signal);
      if (mounted.current) setReceipt(result.id);
    } catch (err) {
      if (mounted.current) setError(controller.signal.aborted
        ? 'This is taking longer than expected. Your draft is still here. Please try again.'
        : getErrorMessage(err, 'We couldn’t send your feedback. Please try again.'));
    } finally {
      window.clearTimeout(timeout);
      submission.current = null;
      sending.current = false;
      if (mounted.current) setSubmitting(false);
    }
  };

  return (
    <UpdatesContext.Provider value={{ openUpdates: () => open('updates'), openFeedback: () => open('feedback'), hasUnread, setOnboardingPending }}>
      {children}
      <WhatsNewDialog isOpen={allowed && view === 'updates' && !otherDialog} onClose={close}
        onFeedback={() => open('feedback', latest?.id)} releases={releases} />
      {draft && <FeedbackDialog isOpen={allowed && view === 'feedback' && !otherDialog} onClose={close}
        draft={draft} onChange={next => { setDraft({ ...next, requestId: crypto.randomUUID() }); setError(''); }}
        onSubmit={submit} submitting={submitting} error={error} receipt={receipt}
        onReset={() => { setDraft(freshDraft(draft.releaseId)); setReceipt(''); setError(''); }} />}
    </UpdatesContext.Provider>
  );
}

export default function ProductUpdatesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  return <UpdatesSession key={user?.id || 'signed-out'}>{children}</UpdatesSession>;
}
